import React from 'react';
import { getLoggedUser } from '../../../../state/user';
import i18n from '../../../../i18n';
import { getAllSectors } from '../../../../models/sectors';
import { SideBar } from '../../sidebar';
import FilterSection from '../../../common/Filter';
import Dropdown from '../../../common/DropDown';
import NoData from '../../../common/NoData';
import { TableSkeleton } from '../../../common/skelton/TableSkelton';
import { getDeliveryGroups } from './services/auctionApprovalServices';
import { tableStyles } from '../../../../styles/components/js/tableStyle';
import DataTable from 'react-data-table-component';
import { apiUrl } from '../../../../constants/global';
import { NavLink } from 'react-router-dom';
import { AuctionAddFundModal } from './components/AuctionAddFundModal';
import { FinancialBreakdownCard } from './components/FinancialBreakdownCard';
import HeaderBanner from '../../../common/HeaderBanner';
import { Download, FileSpreadsheet, Printer } from 'lucide-react';
import NewDropdown from '../../../common/NewDropDown';
import { downloadFile } from '../../../../utils/downloadFile';
import { DeliveryStatus } from '../../../../constants/status';
import { getClients } from '../CashFlowDashboard/services/cashFlowService';
import Select from 'react-select';
import { selectStyles } from '../../../../styles/components/js/selectStyle';
import DeliveryStatusCard from './components/DeliveryStatusCard';
import DeliveryStatusDetailsModal from './components/DeliveryStatusDetailsModal';
import { getGroupList } from '../Group/services/groupServices';

const user = getLoggedUser();
const isClientRole = user?.role === 'C';
const BREAKDOWN_ROLES = ['F', 'GM', 'OM', 'CE'];
const canSeeBreakdown = BREAKDOWN_ROLES.includes(user?.role);

const roleAllowedStatuses = {
  PM: ['pending', 'approved', 'fundAdded', 'financeSettled', 'all'],
  OM: ['pending', 'approved', 'fundAdded', 'financeSettled', 'all'],
  GM: ['pending', 'approved', 'fundAdded', 'financeSettled', 'all'],
  CE: ['pending', 'approved', 'fundAdded', 'financeSettled', 'all'],
  F: ['approved', 'fundAdded', 'fundDeduct', 'financeSettled', 'all'],
  C: ['pending', 'approved', 'all'],
};

const STATUS_TITLES = {
  all: 'all',
  pending: 'client_approval_pending',
  approved: 'client_approved',
  fundAdded: 'fundAdded',
  fundDeduct: 'fund_deduct',
  financeSettled: 'completed',
};

// one status card + its financial breakdown, from the API meta block
const buildCard = (type, m = {}) => ({
  type,
  title: i18n.t(STATUS_TITLES[type]),
  amount: m?.final_bid_amount ?? 0,
  auctionCount: m?.auctions_count ?? 0,
  clientsCount: m?.clients_count ?? 0,
  biddersCount: m?.bidders_count ?? 0,
  paidAmount: m?.bidder_paid_amount ?? 0,
  pendingAmount: m?.bidder_pending_amount ?? 0,
  clientAmount: m?.client_amount ?? 0,
  // breakdown (internal roles only – the API omits these for clients)
  bidAmount: m?.total_bid_amount ?? 0,
  bidVat: m?.bid_vat_amount ?? 0,
  serviceCharge: m?.service_charge_amount ?? m?.profit ?? 0,
  serviceVat: m?.service_charge_vat_amount ?? 0,
  bidderTotal: m?.bidder_total_amount ?? 0,
  profit: m?.profit ?? null,
});

const allowedStatuses = user ? roleAllowedStatuses[user.role] || [] : [];
const filteredMeta = Object.keys(STATUS_TITLES)
  .map((type) => buildCard(type))
  .filter((card) => allowedStatuses.includes(card.type));
const DEFAULT_STATUS = filteredMeta[0]?.type || 'approved';

// 'all' card → every status this role can see; used by the client + group dropdown lists
const deliveryStatusParam = (status) =>
  status === 'all' ? allowedStatuses.filter((s) => s !== 'all').join(',') : status;

// ids are always strings ('' = all) so URL values and option values compare cleanly
const EMPTY_SEARCH = {
  client: '',
  group: '',
  sector: '',
  startDate: null,
  endDate: null,
};

const toMoney = (v) => (Number(v) || 0).toFixed(3);
const groupLabel = (g) => (g.project_code ? `${g.group_name} (${g.project_code})` : g.group_name);

export class AuctionApprovalList extends React.Component {
  constructor() {
    super();
    this.state = {
      search: { ...EMPTY_SEARCH }, // what is typed in the filter bar
      appliedSearch: { ...EMPTY_SEARCH }, // what the current table/cards were loaded with
      clients: [],
      clientLoading: false,
      groups: [],
      groupLoading: false,
      sectors: [],
      deliveryGroups: [],
      status: DEFAULT_STATUS,
      activeTab: DEFAULT_STATUS,
      cardMeta: filteredMeta,
      isLoading: false,
      exportLoading: false,
      showAddFund: false,
      limit: 15,
      page: 1,
      total: 0,
      details: { open: false, type: null, tab: 'auctions' },
    };
    this.clientRequestId = 0;
    this.groupRequestId = 0;
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    const params = new URLSearchParams(window.location.search);
    const search = {
      client: params.get('client') || '',
      group: params.get('group') || '',
      sector: params.get('sector') || '',
      startDate: params.get('startDate') || null,
      endDate: params.get('endDate') || null,
    };
    // ignore a status in the URL that this role can't see
    const urlStatus = params.get('status');
    const status = allowedStatuses.includes(urlStatus) ? urlStatus : DEFAULT_STATUS;
    const page = parseInt(params.get('page')) || 1;
    const limit = parseInt(params.get('limit')) || 15;

    this.setState({ search, status, page, limit, activeTab: status }, () => {
      this.getAllSectors();
      this.reloadFilterLists();
      this.getDeliveryGroups();
    });
  }

  updateUrlQuery(q) {
    const queryString = new URLSearchParams(
      Object.fromEntries(Object.entries(q).filter(([_, v]) => v !== '' && v !== null))
    ).toString();
    window.history.replaceState(null, '', `${window.location.pathname}?${queryString}`);
  }

  async getDeliveryGroups() {
    this.setState({ isLoading: true });
    const { limit, page, status, search } = this.state;
    const { client, group, sector, startDate, endDate } = search;

    const q = { page, limit };
    if (status && status !== 'all') q.status = status;
    if (client) q.client = client;
    if (group) q.group = group;
    if (sector) q.sector = sector;
    if (startDate) q.startDate = startDate;
    if (endDate) q.endDate = endDate;

    // keep 'all' in the URL so a refresh / back link reopens the same card
    this.updateUrlQuery({ ...q, status });

    try {
      const response = await getDeliveryGroups(q);
      if (response?.success) {
        const meta = response.meta || {};
        this.setState({
          deliveryGroups: response.data || [],
          // group included → details modal + exports follow the group filter
          appliedSearch: { client, group, sector, startDate, endDate },
          total: meta.total || 0,
          cardMeta: Object.keys(STATUS_TITLES)
            .map((type) => buildCard(type, meta[type]))
            .filter((card) => allowedStatuses.includes(card.type)),
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      this.setState({ isLoading: false });
    }
  }

  async getAllSectors() {
    try {
      const response = await getAllSectors();
      this.setState({ sectors: response?.data || [] });
    } catch (e) {
      this.setState({ sectors: [] });
    }
  }

  // client + group dropdowns follow the same card / sector / date filters
  reloadFilterLists() {
    this.fetchClientList();
    this.fetchGroupList();
  }

  async fetchClientList() {
    if (isClientRole) return; // clients don't get the client filter

    const requestId = ++this.clientRequestId;
    this.setState({ clientLoading: true });

    try {
      const { status, search } = this.state;
      const { startDate, endDate, sector, group } = search;

      const q = { type: 'deliverySummary' };
      const deliveryStatus = deliveryStatusParam(status);
      if (deliveryStatus) q.deliveryStatus = deliveryStatus;
      if (sector) q.sector = sector;
      if (group) q.group = group; // group chosen first → only its client
      if (startDate) q.startDate = startDate;
      if (endDate) q.endDate = endDate;

      const response = await getClients(q);

      // a newer request was fired while this one was in flight – drop this result
      if (requestId !== this.clientRequestId) return;

      if (response?.success) {
        const clients = [
          { value: '', label: i18n.t('all') || 'All' },
          ...(response.data || []).map((item) => ({
            value: String(item.id),
            label: item.organization_name,
          })),
        ];

        this.setState((prev) => {
          // selected client no longer in the list (other sector / dates / group) → reset to all
          const stillThere = clients.some((c) => c.value === String(prev.search.client || ''));
          return {
            clients,
            search: stillThere ? prev.search : { ...prev.search, client: '' },
          };
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      if (requestId === this.clientRequestId) this.setState({ clientLoading: false });
    }
  }

  async fetchGroupList() {
    const requestId = ++this.groupRequestId;
    this.setState({ groupLoading: true });

    try {
      const { status, search } = this.state;
      const { client, sector, startDate, endDate } = search;

      const q = { type: 'deliverySummary' };
      const deliveryStatus = deliveryStatusParam(status);
      if (deliveryStatus) q.deliveryStatus = deliveryStatus;
      if (sector) q.sector = sector;
      if (startDate) q.startDate = startDate;
      if (endDate) q.endDate = endDate;
      // client role → backend scopes to their own organization; never send one from here
      if (!isClientRole && client) q.organization = client;

      const response = await getGroupList(q);
      if (requestId !== this.groupRequestId) return;

      const groups = response?.success ? response.data || [] : [];
      this.setState((prev) => ({
        groups,
        // selected group no longer in the list → reset
        search: groups.some((g) => String(g.id) === String(prev.search.group))
          ? prev.search
          : { ...prev.search, group: '' },
      }));
    } catch (e) {
      console.error(e);
      if (requestId === this.groupRequestId) this.setState({ groups: [] });
    } finally {
      if (requestId === this.groupRequestId) this.setState({ groupLoading: false });
    }
  }

  getActiveCard() {
    const { cardMeta, activeTab } = this.state;
    return cardMeta.find((c) => c.type === activeTab) || cardMeta[0];
  }

  getColumns = () => {
    const { page, limit } = this.state;

    let columns = [
      {
        name: 'Sl No',
        width: '90px',
        wrap: true,
        cell: (row, i) => (page - 1) * limit + i + 1, // continues across pages
      },
      {
        name: 'ID',
        width: '80px',
        sortable: true,
        wrap: true,
        cell: (row) => row.id,
      },
      {
        minWidth: '130px',
        name: i18n.t('project_code'),
        cell: (row) => <span className="highlight-table-value">{row.project_code}</span>,
      },
      {
        name: i18n.t('project_duration'),
        minWidth: '140px',
        cell: (row) => {
          if (!row.actual_start_date || !row.expected_end_date) return '-';
          const start = new Date(row.actual_start_date);
          const end = new Date(row.expected_end_date);
          const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
          return `${diffDays} days`;
        },
        wrap: true,
        center: true,
      },
      {
        minWidth: '130px',
        name: i18n.t('group_name'),
        cell: (row) => row.group_name,
      },
      {
        name: i18n.t('client'),
        cell: (row) => row.organization_name,
        wrap: true,
      },
      {
        name: i18n.t('auctions'),
        width: '90px',
        cell: (row) => row.auctions_count,
        wrap: true,
        center: true,
      },
      {
        name: i18n.t('start_date'),
        cell: (row) => row.start_date,
        wrap: true,
      },
      {
        name: i18n.t('end_date'),
        cell: (row) => row.end_date,
        wrap: true,
      },
      {
        minWidth: '130px',
        name: i18n.t('total_amount'),
        key: 'total_amount',
        cell: (row) => <span className="highlight-table-value">{toMoney(row.total_amount)}</span>,
        wrap: true,
        right: true,
      },
      {
        minWidth: '130px',
        name: i18n.t('client_amount'),
        cell: (row) => <span className="highlight-table-value">{toMoney(row.client_amount)}</span>,
        wrap: true,
        right: true,
      },
      {
        name: i18n.t('action'),
        wrap: true,
        minWidth: '180px',
        cell: (row) => (
          <div className="action-buttons">
            {getActionButtons(row, this.state.status, user, this)}
          </div>
        ),
      },
    ];

    if (isClientRole) {
      columns = columns.filter((col) => col.key !== 'total_amount');
    }

    return columns;
  };

  handleCardSelect = (cardType) => {
    this.setState({ activeTab: cardType, status: cardType, page: 1 }, () => {
      this.getDeliveryGroups();
      this.reloadFilterLists(); // which clients / groups exist depends on the card
    });
  };

  // sector + dates change which clients and groups exist → reload both lists
  handleSearchChange = (e) => {
    const { name, value } = e.target;
    this.setState(
      (prev) => ({ search: { ...prev.search, [name]: value } }),
      () => {
        if (['sector', 'startDate', 'endDate'].includes(name)) this.reloadFilterLists();
      }
    );
  };

  // client → narrows the group list
  handleClientChange = (selectedOption) => {
    const client = selectedOption?.value ? String(selectedOption.value) : '';
    this.setState(
      (prev) => ({ search: { ...prev.search, client } }),
      () => this.fetchGroupList()
    );
  };

  // group → narrows the client list
  // group → auto-select its client, then narrow both lists
  handleGroupChange = (selectedOption) => {
    const group = selectedOption?.value ? String(selectedOption.value) : '';
    const picked = this.state.groups.find((g) => String(g.id) === group);

    this.setState(
      (prev) => ({
        search: {
          ...prev.search,
          group,
          // clients have no client filter; internal roles get the group's client
          client:
            !isClientRole && picked?.organization
              ? String(picked.organization)
              : prev.search.client,
        },
      }),
      () => this.reloadFilterLists()
    );
  };

  handleSearch = () => {
    this.setState({ page: 1 }, () => this.getDeliveryGroups());
  };

  handleClear = () => {
    this.setState({ search: { ...EMPTY_SEARCH }, page: 1 }, () => {
      this.getDeliveryGroups();
      this.reloadFilterLists();
    });
  };

  onChangePage = (page) => {
    this.setState({ page }, () => this.getDeliveryGroups());
  };

  onChangeRowsPerPage = (limit) => {
    this.setState({ limit, page: 1 }, () => this.getDeliveryGroups());
  };

  onAddFund = (group) => {
    this.setState({ showAddFund: true, selectedGroup: group });
  };

  openDetails = (type, tab = 'auctions') => this.setState({ details: { open: true, type, tab } });
  closeDetails = () => this.setState((p) => ({ details: { ...p.details, open: false } }));

  // exports use the APPLIED filters, so the file always matches the table on screen
  buildExportParams(fileType) {
    const { status, appliedSearch } = this.state;
    return Object.fromEntries(
      Object.entries({
        client: appliedSearch.client,
        group: appliedSearch.group,
        sector: appliedSearch.sector,
        startDate: appliedSearch.startDate,
        endDate: appliedSearch.endDate,
        status: status === 'all' ? null : DeliveryStatus[status],
        fileType,
      }).filter(([_, v]) => v !== null && v !== undefined && v !== '')
    );
  }

  handleDownload = async (fileType) => {
    this.setState({ exportLoading: true });
    try {
      await downloadFile({
        url: `${apiUrl}v2/report/client-payment/groups`,
        params: this.buildExportParams(fileType),
        token: user.token,
        fileType,
        fileName: 'Group_Auction_Delivery',
      });
    } finally {
      this.setState({ exportLoading: false });
    }
  };

  handleDownloadAuction = async (fileType) => {
    this.setState({ exportLoading: true });
    try {
      await downloadFile({
        url: `${apiUrl}v2/report/client-payment/groups/auctions`,
        params: this.buildExportParams(fileType),
        token: user.token,
        fileType,
        fileName: 'Auction_Delivery',
      });
    } finally {
      this.setState({ exportLoading: false });
    }
  };

  renderGroupSelect() {
    const { groups, groupLoading, search } = this.state;

    const options = [
      { value: '', label: i18n.t('all') || 'All' },
      ...groups.map((g) => ({ value: String(g.id), label: groupLabel(g) })),
    ];
    const selected = search.group
      ? options.find((o) => o.value === String(search.group)) || null
      : null;

    return (
      <div className="react-select-wrapper">
        <Select
          options={options}
          value={selected}
          onChange={this.handleGroupChange}
          name="group"
          styles={selectStyles}
          menuPlacement="auto"
          menuPortalTarget={document.body}
          placeholder={groupLoading ? `${i18n.t('loading')}...` : i18n.t('all')}
          isLoading={groupLoading}
          isDisabled={groupLoading}
          isClearable={Boolean(search.group)}
        />
        <label className="react-select-label">{i18n.t('auction_group')}</label>
      </div>
    );
  }

  renderListContent() {
    const { clients, sectors, deliveryGroups, clientLoading, search, isLoading, appliedSearch } =
      this.state;

    return (
      <>
        {/* status cards – overview, comparable side by side */}
        <div className="ds-cards">
          {this.state.cardMeta.map((config) => (
            <DeliveryStatusCard
              key={config.type}
              {...config}
              showClients={!isClientRole}
              loading={isLoading}
              selected={this.state.activeTab === config.type}
              onSelect={this.handleCardSelect}
              onDetails={this.openDetails}
            />
          ))}
        </div>

        {/* financial breakdown – detail for the selected status (internal only) */}
        {canSeeBreakdown && (
          <FinancialBreakdownCard
            card={this.getActiveCard()}
            loading={isLoading}
            startDate={appliedSearch.startDate}
            endDate={appliedSearch.endDate}
          />
        )}

        <FilterSection loading={isLoading} onSearch={this.handleSearch} onClear={this.handleClear}>
          {/* Start Date */}
          <div className="select-wrapper floating-label-wrapper">
            <input
              id="startDate"
              type="date"
              name="startDate"
              value={search.startDate || ''}
              max={search.endDate || undefined}
              onChange={this.handleSearchChange}
              className="filter-base floating-input"
              placeholder=" "
            />
            <label htmlFor="startDate" className="floating-label">
              {i18n.t('start_date')}
            </label>
          </div>

          {/* End Date */}
          <div className="select-wrapper floating-label-wrapper">
            <input
              id="endDate"
              type="date"
              name="endDate"
              value={search.endDate || ''}
              min={search.startDate || undefined}
              onChange={this.handleSearchChange}
              className="filter-base floating-input"
              placeholder=" "
            />
            <label htmlFor="endDate" className="floating-label">
              {i18n.t('end_date')}
            </label>
          </div>

          {/* Sector Dropdown */}
          <Dropdown
            name="sector"
            value={search.sector}
            onChange={this.handleSearchChange}
            placeholder={i18n.t('sector')}
            empty={i18n.t('all')}
            options={sectors.map((s) => ({
              value: s.id,
              label: s.name || s.sector_name || s.title,
            }))}
          />

          {/* Client Dropdown */}
          {!isClientRole && (
            <div className="react-select-wrapper">
              <Select
                options={clients}
                value={clients.find((c) => c.value === String(search.client || '')) || null}
                onChange={this.handleClientChange}
                name="client"
                styles={selectStyles}
                menuPlacement="auto"
                menuPortalTarget={document.body}
                placeholder={clientLoading ? `${i18n.t('loading')}...` : i18n.t('all')}
                isLoading={clientLoading}
                isDisabled={clientLoading}
                isClearable={Boolean(search.client)}
              />
              <label className="react-select-label">{i18n.t('client')}</label>
            </div>
          )}

          {/* Group Dropdown – all groups, or only the selected client's */}
          {this.renderGroupSelect()}
        </FilterSection>

        <div className="Data-Listing">
          {isLoading ? (
            <TableSkeleton columns={10} rows={10} />
          ) : deliveryGroups.length ? (
            <DataTable
              responsive={true}
              columns={this.getColumns()}
              data={deliveryGroups}
              customStyles={tableStyles}
              highlightOnHover
              pagination
              paginationServer
              paginationTotalRows={this.state.total}
              paginationPerPage={this.state.limit}
              paginationDefaultPage={this.state.page}
              paginationRowsPerPageOptions={[15, 25, 50, 100]}
              paginationComponentOptions={{
                rowsPerPageText: i18n.t('rows_per_page'),
                rangeSeparatorText: i18n.t('out'),
              }}
              onChangePage={this.onChangePage}
              onChangeRowsPerPage={this.onChangeRowsPerPage}
            />
          ) : (
            <NoData
              message={i18n.t('no_data_available')}
              actionText={i18n.t('refresh')}
              onAction={() => this.getDeliveryGroups()}
            />
          )}
        </div>
      </>
    );
  }

  renderExportAction() {
    const { exportLoading } = this.state;

    return (
      <NewDropdown
        label={i18n.t('export')}
        loading={exportLoading}
        icon={Download}
        items={[
          {
            label: i18n.t('excel'),
            icon: FileSpreadsheet,
            onClick: () => this.handleDownload('excel'),
          },
          {
            label: i18n.t('excel_auctions'),
            icon: FileSpreadsheet,
            onClick: () => this.handleDownloadAuction('excel'),
          },
          {
            label: i18n.t('pdf'),
            icon: Printer,
            disabled: true,
          },
        ]}
      />
    );
  }

  renderHeaderAction() {
    return <div className="header-action">{this.renderExportAction()}</div>;
  }

  render() {
    const { showAddFund, selectedGroup, details, cardMeta, appliedSearch } = this.state;

    const modal = (
      <AuctionAddFundModal
        show={showAddFund}
        onClose={() => {
          this.getDeliveryGroups();
          this.setState({ showAddFund: false, selectedGroup: null });
        }}
        group={selectedGroup}
      />
    );

    // rendered for every role – clients open it from the cards too
    const detailsModal = (
      <DeliveryStatusDetailsModal
        open={details.open}
        type={details.type}
        initialTab={details.tab}
        title={cardMeta.find((c) => c.type === details.type)?.title}
        filters={appliedSearch}
        isClientRole={isClientRole}
        onClose={this.closeDetails}
      />
    );

    if (isClientRole) {
      return (
        <React.Fragment>
          <div className="cd-page">
            <div className="cd-shell">
              <p className="cd-crumb">
                <NavLink to="/client-management">{i18n.t('dashboard')}</NavLink>
                <span className="cd-crumb__current">{i18n.t('auction_approval')}</span>
              </p>

              <header className="cd-hero cd-hero--compact">
                <h1 className="cd-hero__title">{i18n.t('auction_approval')}</h1>
                <p className="cd-hero__sub">{i18n.t('track_auction_approval')}</p>
              </header>

              <div className="card">
                <div className="card-header">{this.renderHeaderAction()}</div>
                <div className="card-body">{this.renderListContent()}</div>
              </div>
            </div>
          </div>
          {detailsModal}
          {modal}
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        <HeaderBanner
          title={user.role === 'F' ? i18n.t('client_payment') : i18n.t('auction_approval')}
        />
        <section className="dashboard-section">
          <div className="container">
            <div className="row">
              <div className="col-xl-3 col-lg-12 col-md-12">
                {!this.state.hideSidebar && <SideBar />}
              </div>
              <div
                className={
                  this.state.hideSidebar
                    ? 'col-xl-12 col-lg-12 col-md-12'
                    : 'col-xl-9 col-lg-12 col-md-12'
                }
              >
                <div className="card">
                  <div className="card-header">
                    <div className="expand-dashboard">
                      <span
                        style={{ fontSize: '1rem', marginRight: '0.5rem', cursor: 'pointer' }}
                        onClick={() => this.setState({ hideSidebar: !this.state.hideSidebar })}
                      >
                        {this.state.hideSidebar && <i className="icon-size-actual"></i>}
                        {!this.state.hideSidebar && <i className="icon-size-fullscreen"></i>}
                      </span>
                    </div>
                    {this.renderHeaderAction()}
                  </div>
                  <div className="card-body">{this.renderListContent()}</div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {detailsModal}
        {modal}
      </React.Fragment>
    );
  }
}

const getActionButtons = (row, status, user, ctx) => {
  const buttons = [];

  const isPM = user.role === 'PM';
  const isFinance = user.role === 'F';
  const isClient = user.role === 'C';

  const hasAuctions = (row.auctions || []).length > 0;

  // carry the list's exact URL (applied filters + status + page + limit) to the details page
  const detailParams = new URLSearchParams({ status: ctx.state.status });
  if (ctx.state.appliedSearch.sector) detailParams.set('sector', ctx.state.appliedSearch.sector);
  detailParams.set('back', window.location.search);
  const detailPath = `/auction_approval/${row.id}?${detailParams.toString()}`;

  // PENDING
  if (status === 'pending' && (isPM || isClient)) {
    buttons.push(<ActionButton key="process" label="process" to={detailPath} />);
  }

  // APPROVED
  if (status === 'approved' && (isPM || isClient || isFinance)) {
    buttons.push(<ActionButton key="view" label="view" to={detailPath} />);
  }

  // FUND ADDED / FUND DEDUCT
  if (status === 'fundAdded' || status === 'fundDeduct') {
    if (isFinance) {
      buttons.push(<ActionButton key="process" label="process" to={detailPath} />);
    }
    if (isPM) {
      buttons.push(<ActionButton key="view" label="view" to={detailPath} />);
    }
  }

  // ALL – always allow viewing
  if (status === 'all') {
    buttons.push(<ActionButton key="view" label="view" to={detailPath} />);
  }

  // COMPLETED
  if (status === 'financeSettled' && hasAuctions) {
    buttons.push(<ActionButton key="view" label="view" to={detailPath} />);
    buttons.push(
      <a
        key="invoice"
        className="button1 button1-primary button1-action"
        href={`${apiUrl}auction_approval/invoice?group=${row.id}`}
        target="_blank"
        rel="noreferrer"
      >
        {i18n.t('invoice')}
      </a>
    );
  }

  return buttons;
};

const ActionButton = ({ label, icon, to, variant = 'button1-primary', onClick }) => {
  if (to) {
    return (
      <NavLink to={to} className={`button1 ${variant} button1-action`}>
        {icon} {i18n.t(label)}
      </NavLink>
    );
  }

  return (
    <button className={`button1 ${variant} button1-action`} onClick={onClick}>
      {icon} {i18n.t(label)}
    </button>
  );
};
